import React, { useState, useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { message, Input, Form as AntForm, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { isEmail, splitFullName } from '@/utils';

import { patchUser, logOut } from '@/store/user/thunkActions';
import { useTypedDispatch, useTypedSelector } from '@/store';
import { closeSettings } from '@/store/core';

import type { Logo, ValidationErrorsObject } from '@/types/global';

const { TextArea } = Input;

const UserForm = () => {
  const [t] = useTranslation();
  const dispatch = useTypedDispatch();

  const fileInput = useRef<HTMLInputElement>(null);

  const { user, token } = useTypedSelector(state => state.authUser);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const onFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, changeCallback: (prop: any) => void) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append('file', file);
    try {
      const response = (
        await axios({
          method: 'post',
          url: import.meta.env.VITE_FILE_URL,
          data,
          headers: { Authorization: `Bearer ${token}` },
        })
      ).data[0];

      changeCallback(response);
    } catch (e: any) {
      setErrorMessage(e.message);
    }
  };

  const saveSettings = async (values: {
    name: string;
    email: string;
    description: string;
    logo: Logo;
  }) => {
    const [f, l] = splitFullName(values.name);

    const data = {
      _id: user._id,
      description: values.description,
      logoId: values.logo ? values.logo._id : null,
      firstName: f,
      lastName: l,
    };

    try {
      await dispatch(patchUser(data));
      return {};
    } catch (e: any) {
      setErrorMessage(e.message);
      return e.errors;
    }
  };

  const onLogout = async () => {
    try {
      await dispatch(logOut());
      dispatch(closeSettings());
    } catch {
      message.error('Error while logging out');
    }
  };

  const initialValues = { ...user, name: `${user.firstName} ${user.lastName}` };

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={saveSettings}
      validate={validate}
      render={({ handleSubmit }) => (
        <AntForm
          layout="vertical"
          onFinish={handleSubmit}
          className="settings"
        >
          <div className="settings__row settings__row_error">
            <div className="settings__error">
              <p>{errorMessage}</p>
            </div>
          </div>
          <div className="settings__row">
            <div className="settings__box settings__box_image">
              <Field name="logo">
                {({ input }) => (
                  <div>
                    <div
                      className="logo"
                      style={{
                        backgroundImage: input.value?.path ? `url(${input.value.path})` : 'none',
                      }}
                      onClick={() => fileInput.current?.click()}
                      role="presentation"
                    />
                    <input
                      ref={fileInput}
                      type="file"
                      name="file"
                      className="input-file"
                      style={{ display: 'none' }}
                      onChange={e => onFileUpload(e, input.onChange)}
                    />
                  </div>
                )}
              </Field>
            </div>
            <div className="settings__box">
              <div className="settings__field settings__field_padding">
                <Field name="name">
                  {({ input, meta }) => (
                    <AntForm.Item
                      label={t('Name')}
                      validateStatus={meta.touched && (meta.error || meta.submitError) ? 'error' : ''}
                      help={meta.touched && (meta.error || meta.submitError) ? (meta.error || meta.submitError) : ''}
                    >
                      <Input
                        {...input}
                        placeholder={t('Name')}
                        className={meta.touched && (meta.error || meta.submitError) ? 'field_error' : ''}
                      />
                    </AntForm.Item>
                  )}
                </Field>
              </div>
              <div className="settings__field">
                <Field name="email">
                  {({ input, meta }) => (
                    <AntForm.Item
                      label={t('Email')}
                      validateStatus={meta.touched && (meta.error || meta.submitError) ? 'error' : ''}
                      help={meta.touched && (meta.error || meta.submitError) ? (meta.error || meta.submitError) : ''}
                    >
                      <Input
                        {...input}
                        placeholder={t('Email')}
                        disabled
                        className={meta.touched && (meta.error || meta.submitError) ? 'field_error' : ''}
                      />
                    </AntForm.Item>
                  )}
                </Field>
              </div>
            </div>
            <div className="settings__box">
              <div className="settings__field settings__field_full">
                <Field name="description">
                  {({ input, meta }) => (
                    <AntForm.Item
                      label={t('Description')}
                      validateStatus={meta.touched && (meta.error || meta.submitError) ? 'error' : ''}
                      help={meta.touched && (meta.error || meta.submitError) ? (meta.error || meta.submitError) : ''}
                    >
                      <TextArea
                        {...input}
                        placeholder={t('Description')}
                        autoSize={{ minRows: 3, maxRows: 6 }}
                      />
                    </AntForm.Item>
                  )}
                </Field>
              </div>
            </div>
          </div>
          <div className="settings__row settings__row_justify-between">
            <Button className="settings__save" type="primary" htmlType="submit">
              {t('save')}
            </Button>
            <Button className="settings__save" onClick={onLogout} type="default">
              {t('logout')}
            </Button>
          </div>
        </AntForm>
      )}
    />
  );
};

type FormState = {
  email: string;
  name: string;
};

const validate = (values: Partial<FormState>) => {
  const errors: ValidationErrorsObject<FormState> = {};

  if (!values.email) {
    errors.email = 'Required';
  }
  if (values.email && !isEmail(values.email)) {
    errors.email = 'Not valid email';
  }
  if (!values.name) {
    errors.name = 'Required';
  }
  if (!values.name || values.name.split(' ').length < 2) {
    errors.name = 'Введите полное имя';
  }

  return errors;
};

export default UserForm;
